import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  ClipBoardWrapper,
  Input,
  Select,
  TextEditor,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { messageTemplatesService } from "../../../../api";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Content,
  FormSection,
  Header,
  SidebarSection,
  VariableTag,
  VariablesHint,
  VariablesWrapper,
  FormActions,
} from "./styles";
import { extractErrorMessage } from "../../../../utils/error-extractor";

type CreateOptionsCategory = {
  id: string;
  name: string;
  description: string;
};

type MessageTemplateCreateOptions = {
  languages: Array<{
    language_display: string;
    language_code: string;
  }>;
  target: Record<string, string>;
  target_category: Record<string, string>;
  categories: CreateOptionsCategory[];
  category_variables: Record<string, string[]>;
};

const MessageTemplateCreate: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  usePageTitle(t("CREATE_TEMPLATE"));

  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [createOptions, setCreateOptions] = useState<MessageTemplateCreateOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  useEffect(() => {
    setOptionsLoading(true);
    messageTemplatesService
      .getCreateOptions()
      .then((res) => {
        setCreateOptions(res.data as MessageTemplateCreateOptions);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_CREATE_OPTIONS");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/settings/message-templates");
      })
      .finally(() => {
        setOptionsLoading(false);
      });
  }, [t, viewAlert, navigate]);

  const targetOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    return Object.entries(createOptions.target || {}).map(([value, label]) => ({
      id: value,
      value,
      label,
    }));
  }, [createOptions]);

  const targetCategoryOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    const entries = Object.entries(createOptions.target_category || {});
    return [
      { id: "", value: "", label: t("NONE") },
      ...entries.map(([value, label]) => ({ id: value, value, label })),
    ];
  }, [createOptions, t]);

  const categoryOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    return (createOptions.categories || []).map((category) => ({
      id: category.id,
      value: category.id,
      name: category.name,
      label: `${category.name}${category.description ? ` - ${category.description}` : ''}`,
    }));
  }, [createOptions]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("REQUIRED_FIELD")),
        target: yup.string().required(t("REQUIRED_FIELD")),
        category: yup.string().required(t("REQUIRED_FIELD")),
        title: yup.string().required(t("REQUIRED_FIELD")),
        content: yup.string().required(t("REQUIRED_FIELD")),
        target_category: yup.string().nullable(),
      }),
    [t]
  );

  const formik = useFormik({
    enableReinitialize: false,
    validationSchema,
    initialValues: {
      name: "",
      target: "",
      target_category: "",
      category: "",
      title: "",
      content: "",
    },
    onSubmit: (formValues) => {
      setCreateLoading(true);
      setPageLoading(true);
      const payload = {
        name: formValues.name,
        target: formValues.target,
        target_category: formValues.target_category || "",
        category: formValues.category,
        title: formValues.title,
        content: formValues.content,
      };
      messageTemplatesService
        .create(payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("TEMPLATE_CREATED_SUCCESSFULLY"),
          });
          navigate("/settings/message-templates");
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_CREATE_TEMPLATE"),
            });
          }
        })
        .finally(() => {
          setCreateLoading(false);
          setPageLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setErrors,
  } = formik;

  const selectedCategory = useMemo(() => {
    if (!createOptions) {
      return null;
    }
    return (createOptions.categories || []).find(
      (category) => category.id === values.category
    ) || null;
  }, [createOptions, values.category]);

  const variables = useMemo(() => {
    if (!createOptions || !selectedCategory) {
      return [];
    }
    const variablesMap = createOptions.category_variables || {};
    return variablesMap[selectedCategory.name] || [];
  }, [createOptions, selectedCategory]);

  const onFormSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    formik.submitForm();
  }, [formik]);

  if (optionsLoading || !createOptions) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("OPTIONS_LOADING")}</Typography>
      </Container>
    );
  }

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath="/settings/message-templates" />
        <Typography variant="h1">{t("CREATE_TEMPLATE")}</Typography>
      </Header>
      <Content>
        <FormSection as="form" onSubmit={onFormSubmit}>
          <Input
            required
            title={t("NAME")}
            placeholder=""
            fieldName="name"
            {...formikProps}
          />
          <Select
            required
            title={t("TARGET")}
            data={targetOptions}
            selectedValue={values.target}
            onChange={(value: string) => setFieldValue("target", value)}
            placeholder={t("CHOOSE")}
            fieldName="target"
            {...formikProps}
          />
          <Select
            title={t("TARGET_CATEGORY")}
            data={targetCategoryOptions}
            selectedValue={values.target_category}
            onChange={(value: string) => setFieldValue("target_category", value)}
            placeholder={t("CHOOSE")}
            fieldName="target_category"
            {...formikProps}
          />
          <Select
            required
            title={t("CATEGORY")}
            data={categoryOptions}
            selectedValue={values.category}
            onChange={(value: string) => setFieldValue("category", value)}
            placeholder={t("CHOOSE")}
            fieldName="category"
            {...formikProps}
          />
          <Input
            required
            title={t("TITLE")}
            placeholder=""
            fieldName="title"
            {...formikProps}
          />
          <TextEditor
            onChange={(value: string) => setFieldValue("content", value)}
            fieldName="content"
            values={values}
            errors={errors}
          />
          <FormActions>
            <Button
              variant="stroke"
              size="display"
              title={t("CANCEL")}
              onClick={() => navigate("/settings/message-templates")}
            />
            <Button
              variant="primary"
              size="display"
              title={t("CREATE")}
              loading={createLoading}
              onClick={onFormSubmit}
            />
          </FormActions>
        </FormSection>
        <SidebarSection>
          {variables.length > 0 && (
            <VariablesHint>
              <Typography variant="h3">{t("AVAILABLE_VARIABLES")}</Typography>
              <Typography variant="body" style={{ marginBottom: "10px" }}>
                {t("CLICK_TO_COPY_VARIABLE")}
              </Typography>
              <VariablesWrapper>
                {variables.map((variable) => (
                  <ClipBoardWrapper
                    key={variable}
                    toCopy={variable}
                  >
                    <VariableTag title={t("CLICK_TO_COPY")}>
                      {variable}
                    </VariableTag>
                  </ClipBoardWrapper>
                ))}
              </VariablesWrapper>
            </VariablesHint>
          )}
        </SidebarSection>
      </Content>
    </Container>
  );
};

export default MessageTemplateCreate;
